import React from 'react';

import { hasVariantCapability } from '../../capability-matrix';
import { severityLabel } from '../../labels';
import { CoAttendanceModeIndicator } from '../badges/CoAttendanceModeIndicator';
import { PresenceIndicator } from '../badges/PresenceIndicator';
import { ModeDistributionBar } from '../charts/ModeDistributionBar';

import type { AttendantVM, SupervisorVariant } from '../../view-models';

export interface SupervisorAttendantCardProps {
  att: AttendantVM;
  variant: SupervisorVariant;
  onOpenDrawer?: (att: AttendantVM) => void;
  // "Acompanhar" é ação direta: navega p/ o picker de acompanhamento do atendente.
  // O MODO de observação (visível/invisível/público) é escolhido no espelho da Tela 2,
  // onde vive o fluxo com justificativa (o modo invisível exige justificar — D3); por isso
  // não se pré-escolhe aqui (evita prometer uma escolha descartada / burlar a justificativa).
  onOpenAcompanhar?: (att: AttendantVM) => void;
  onMensagemPrivada?: (att: AttendantVM) => void;
  onAtribuir?: (att: AttendantVM) => void;
}

export function SupervisorAttendantCard({
  att,
  variant,
  onOpenDrawer,
  onOpenAcompanhar,
  onMensagemPrivada,
  onAtribuir,
}: SupervisorAttendantCardProps): React.JSX.Element {
  const cardCls = [
    'att-card',
    att.flags.includes('behavioral') && 'att-card--flag-behavioral',
    att.flags.includes('break-glass') && 'att-card--flag-breakglass',
    att.presence === 'away' && 'att-card--away',
  ]
    .filter(Boolean)
    .join(' ');

  const showCoAttendance = hasVariantCapability('tela1.coattendance', variant);
  const showModeBar = hasVariantCapability('tela1.distribuicao-modos', variant);
  const showPresence = hasVariantCapability('tela1.presence-indicator', variant);

  function handleCardClick(e: React.MouseEvent<HTMLDivElement>): void {
    const target = e.target as Element;
    if (target.closest('.att-card__actions') !== null) return;
    onOpenDrawer?.(att);
  }

  return (
    <div
      className={cardCls}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ')
          handleCardClick(e as unknown as React.MouseEvent<HTMLDivElement>);
      }}
      role="button"
      tabIndex={0}
    >
      <div className="att-card__avatar">
        {att.initials}
        {showPresence && (
          <span
            className={`att-card__avatar-presence att-card__avatar-presence--${att.presence}`}
            aria-hidden="true"
          />
        )}
      </div>

      <div className="att-card__body">
        {/* Head: name + role-pill + flags */}
        <div className="att-card__head">
          <span className="att-card__name">{att.name}</span>
          <span className="att-card__role-pill">{att.role.replace(/^Atendente\s+/i, '')}</span>
          <span className="att-card__head-spacer" aria-hidden="true" />
          {att.flags.includes('behavioral') && (
            <span className="att-card__flag-pill att-card__flag-pill--behavioral">
              padrão{' '}
              {att.behavioralPattern !== undefined
                ? severityLabel(att.behavioralPattern.severity)
                : ''}
            </span>
          )}
          {att.flags.includes('handoff-pending') && (
            <span className="att-card__flag-pill att-card__flag-pill--handoff">
              transferência pendente
            </span>
          )}
          {att.flags.includes('break-glass') && (
            <span className="att-card__flag-pill att-card__flag-pill--breakglass">break-glass</span>
          )}
        </div>

        {/* Sub: presence · última ação · specs */}
        <div className="att-card__sub">
          {showPresence && (
            <PresenceIndicator
              presence={att.presence}
              {...(att.awayMinutes !== undefined ? { awayMinutes: att.awayMinutes } : {})}
            />
          )}
          <span className="att-card__sep" aria-hidden="true" />
          <span>
            última ação <b style={{ color: 'var(--fg-2)' }}>{att.lastAction}</b>
          </span>
          <span className="att-card__sep" aria-hidden="true" />
          <span className="att-card__sub-specs">{att.specialties.join(' · ')}</span>
        </div>

        {/* Stats: distribuição modos · SLA · IA dirige */}
        <div className="att-card__stats">
          {att.metricsAvailable === false ? (
            <>
              <span className="att-card__stat-label">carga</span>
              <span>
                {att.loadSummary?.current ?? 0}/{att.loadSummary?.capacity ?? 0}
              </span>
              <span className="att-card__sep" aria-hidden="true" />
              <span style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>
                distribuição — em implementação
              </span>
            </>
          ) : (
            <>
              {showModeBar && (
                <>
                  <span className="att-card__stat-label">distrib.</span>
                  <ModeDistributionBar modes={att.modes} showLabels={true} />
                </>
              )}
              <span className="att-card__sep" aria-hidden="true" />
              <span className="att-card__stat-label">SLA</span>
              <span className={`sla sla--${att.slaStatus}`}>{att.sla}</span>
              {att.iaDirigindo > 0 && (
                <>
                  <span className="att-card__sep" aria-hidden="true" />
                  <span style={{ color: 'var(--ai-fg)', fontWeight: 'var(--fw-medium)' }}>
                    IA · {att.iaDirigindo}
                  </span>
                </>
              )}
            </>
          )}
        </div>

        {/* CoAttendance (3rd line, conditional) */}
        {showCoAttendance && (att.coAttendance !== undefined || att.coAttendedBy !== undefined) && (
          <div className="att-card__coatt">
            {att.coAttendance !== undefined && (
              <>
                <CoAttendanceModeIndicator
                  role={att.coAttendance.role}
                  visibilityMode="visible-to-attendant"
                  composite="shadow+visible-to-attendant"
                />
                <span>
                  monitor → <b style={{ color: 'var(--fg-2)' }}>{att.coAttendance.target}</b>
                </span>
              </>
            )}
            {att.coAttendedBy !== undefined && (
              <span>
                ← monitorada por <b style={{ color: 'var(--fg-2)' }}>{att.coAttendedBy.from}</b>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions: small line at bottom */}
      <div className="att-card__actions">
        <button
          type="button"
          className="btn-ghost-mini btn-ghost-mini--primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpenAcompanhar?.(att);
          }}
        >
          Acompanhar
        </button>
        {/* P19 (Slice E): só renderiza com a cap direct-message-initiate — a página
            só passa o handler quando concedida. Sem a cap, o botão é AUSENTE
            (não stub desabilitado). */}
        {onMensagemPrivada !== undefined && (
          <button
            type="button"
            className="btn-ghost-mini"
            onClick={(e) => {
              e.stopPropagation();
              onMensagemPrivada(att);
            }}
          >
            Mensagem privada
          </button>
        )}
        <button
          type="button"
          className="btn-ghost-mini"
          disabled={onAtribuir === undefined}
          title={onAtribuir === undefined ? 'Em breve' : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onAtribuir?.(att);
          }}
        >
          Atribuir conversa
        </button>
      </div>
    </div>
  );
}
